import React, { useState, useEffect } from 'react';
import routes from 'sitewide-js/core_pre_webpack/routes';
import { createUserFollowing } from 'js/social/api/user_following_api';
import { get } from 'js/utils/api_utils';
import { SuggestedFollowee } from 'js/core_post_webpack/models/user';
import SuggestedUserCard from './SuggestedUserCard';
import { currentUser } from 'js/utils/user';
import SuggestedContentSidebar from './shared/SuggestedContentSidebar';
import { Skeleton } from 'js/design_system/skeleton/Skeleton';

interface Suggestion {
  connecting_users: Array<object>;
  sources: Array<string>;
  user: SuggestedFollowee;
  scoring_metadata?: { [key: string]: any };
}

interface Suggestions {
  following: Array<Suggestion>;
}

interface Props {
  className?: string;
  headerText?: string;
}

const MAX_SUGGESTION_COUNT = 3;

export default function SuggestedAcademics({
  className,
  headerText = 'Who to follow',
}: Props) {
  const [suggestedAcademics, setSuggestedAcademics] = useState<
    Array<Suggestion>
  >([]);
  const [loading, setLoading] = useState(true);
  const [followedAcademics, setFollowedAcademics] = useState<Array<number>>([]);

  const getSuggestedAcademics = (
    count = MAX_SUGGESTION_COUNT,
  ): Promise<Suggestions> => {
    return get(
      routes.api_v0_suggestions_following_index_path({
        subdomain_param: 'api',
        count: count,
        page: 'sidebar',
      }),
    );
  };

  const refreshSuggestedAcademics = (
    newSuggestions: Array<Suggestion>,
    followedId: number,
  ) => {
    setSuggestedAcademics((prevSuggestedAcademics) => {
      const suggestions: Array<Suggestion> = [];
      const candidateNewSuggestions: Array<Suggestion> = newSuggestions.filter(
        (suggestion) => {
          const oldSuggestionIds = prevSuggestedAcademics.map(
            (suggestedAcademic) => suggestedAcademic.user.id,
          );
          return !oldSuggestionIds.includes(suggestion.user.id);
        },
      );

      for (let i = 0; i < MAX_SUGGESTION_COUNT; i++) {
        const suggestion = prevSuggestedAcademics[i];
        if (suggestion !== undefined && suggestion.user.id !== followedId) {
          suggestions.push(suggestion);
        } else if (candidateNewSuggestions.length > 0) {
          suggestions.push(candidateNewSuggestions[0]);
          candidateNewSuggestions.splice(0, 1);
        }
      }

      return suggestions;
    });
  };

  useEffect(() => {
    getSuggestedAcademics().then((res) => {
      setSuggestedAcademics(res.following);
      setLoading(false);
    });
  }, []);

  const followUser = async (followedId: number): Promise<void> => {
    try {
      await createUserFollowing(followedId, 'social_sidebar_suggestion');
      setFollowedAcademics((prevFollowed) => [...prevFollowed, followedId]);
      const res = await getSuggestedAcademics();
      refreshSuggestedAcademics(res.following, followedId);
    } catch {
      refreshSuggestedAcademics([], followedId);
    } finally {
      setFollowedAcademics((prev) => prev.filter((id) => id !== followedId));
    }
  };

  if (suggestedAcademics.length === 0 && !loading) {
    return null;
  }

  return (
    <SuggestedContentSidebar
      viewAllUrl={`${currentUser?.url}/Network?tab=suggested`}
      headerText={headerText || 'Who to follow'}
      className={className}
      impressionSource="soc_home_sugg-a"
    >
      {loading
        ? [...Array(3)].map((_, idx) => (
            <Skeleton key={idx} type="follow-suggestion-sidebar" />
          ))
        : suggestedAcademics.map((suggestion) => {
            return (
              <li key={suggestion.user.id}>
                {followedAcademics.includes(suggestion.user.id) ? (
                  <Skeleton type="follow-suggestion-sidebar" />
                ) : (
                  <SuggestedUserCard
                    user={suggestion.user}
                    followUser={followUser}
                    scoring={suggestion.scoring_metadata}
                  />
                )}
              </li>
            );
          })}
    </SuggestedContentSidebar>
  );
}
